import { usePremiumAccess } from '@hellodarwin/app-core/lib/providers/premium-access-provider';
import {
  FormFillingProps,
  GinStepsProvider,
} from '@hellodarwin/app-features/lib/features/gin-steps/gin-steps-provider';
import PaginatedGinSteps from '@hellodarwin/app-features/lib/features/gin-steps/paginated-gin-steps';
import StepsManagerModal from '@hellodarwin/app-features/lib/features/steps-manager/modal';
import Modal from '@hellodarwin/core/lib/components/common/hd-modal';
import Loading from '@hellodarwin/core/lib/components/loading';
import useLocale from '@hellodarwin/core/lib/features/providers/locale-provider';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { useAppSelector } from '../../../../../app/app-hooks';
import {
  selectGinStepForm,
  selectGinStepsIsLoading,
  selectItemIdsByStepId,
  selectItemsById,
  selectPagesById,
  selectStepIdsByPageId,
  selectStepsById,
} from '../../../../../features/api/slices/gin-steps-slice';
import InvalidGinForm from './invalid-gin-form-modal';
import useFormFillingGinRichTextParseOptions from './use-form-filling-gin-rich-text-parse-options';
import useGinStepUtils from './use-gin-step-utils';

interface GinStepsProps {
  programId: string;
}

const GinSteps = ({ programId }: GinStepsProps) => {
  const { selectedLocale } = useLocale();
  const theme = useTheme();
  const navigate = useNavigate();
  const { isSectionAccessible } = usePremiumAccess();
  const isAccessible = isSectionAccessible('premium');
  const { getRichTextParseOptions, contextHolder } =
    useFormFillingGinRichTextParseOptions(programId);

  // Selectors
  const isLoading = useAppSelector(selectGinStepsIsLoading);
  const ginForm = useAppSelector(selectGinStepForm);
  const stepsById = useAppSelector(selectStepsById);
  const pagesById = useAppSelector(selectPagesById);
  const itemsById = useAppSelector(selectItemsById);
  const stepIdsByPageId = useAppSelector(selectStepIdsByPageId);
  const itemIdsByStepId = useAppSelector(selectItemIdsByStepId);

  const {
    assetApplicationProps,
    propertyItemProps,
    handleSubmitApplicationItem,
    handleDeleteApplicationItem,
    applicationItemStatesById,
    applicationStatusModal,
    closeApplicationStatusModal,
    triggerProgramStatusDialog,
    handleDownloadAsset,
    handleUpdateStepValidation,
  } = useGinStepUtils({
    programId,
  });

  const formFillingProps: FormFillingProps | undefined = useMemo(() => {
    if (
      !assetApplicationProps ||
      !propertyItemProps ||
      !applicationItemStatesById
    )
      return undefined;
    return {
      applicationItemStatesById,
      handleSubmitApplicationItem,
      handleDeleteApplicationItem,
      triggerProgramStatusDialog,
      handleUpdateStepValidation,
      assetApplicationProps,
      propertyItemProps,
    } satisfies FormFillingProps;
  }, [
    applicationItemStatesById,
    handleSubmitApplicationItem,
    handleDeleteApplicationItem,
    triggerProgramStatusDialog,
    handleUpdateStepValidation,
    assetApplicationProps,
    propertyItemProps,
  ]);

  const shouldShowInvalidModal = useMemo(() => {
    if (!ginForm || !ginForm.pages || isLoading) return false;
    for (let i = 1; i <= 4; i++) {
      const steps = ginForm.pages[i]?.steps;
      if (steps && steps.length > 0) {
        return false;
      }
    }
    return true;
  }, [ginForm, isLoading]);

  const handleCloseInvalidModal = () => {
    navigate(`/contact-us`);
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: '24px',
          border: '1px solid',
          borderColor: theme.colors.grey_1,
          backgroundColor: theme.colors.white_1,
          gap: '16px',
        }}
      >
        {isLoading || !formFillingProps ? (
          <Loading />
        ) : (
          <GinStepsProvider
            viewMode={selectedLocale === 'en' ? 'client_en' : 'client_fr'}
            formState={'filling'}
            formFillingProps={formFillingProps}
            customRichTextParseOptions={getRichTextParseOptions}
            downloadAsset={handleDownloadAsset}
            stepsById={stepsById ?? {}}
            itemsById={itemsById ?? {}}
            pagesById={pagesById ?? {}}
            itemIdsByStepId={itemIdsByStepId}
            stepIdsByPageId={stepIdsByPageId}
          >
            {contextHolder}
            <PaginatedGinSteps showAssignmentStatus={false} />
            <StepsManagerModal
              open={applicationStatusModal.open}
              onClose={closeApplicationStatusModal}
              onConfirm={applicationStatusModal.onConfirm}
              neededProperties={applicationStatusModal.neededProperties}
            />
          </GinStepsProvider>
        )}
      </div>
      {isAccessible && (
        <Modal
          open={shouldShowInvalidModal}
          handleCancel={() => {
            navigate(`/applications`);
          }}
        >
          <InvalidGinForm onClose={handleCloseInvalidModal} />
        </Modal>
      )}
    </>
  );
};

export default GinSteps;
